import {
  Dismiss16Regular,
  Info16Regular,
  Warning16Regular,
} from "@fluentui/react-icons";
import { getAnnouncements, markAnnouncementAsSeen } from "api";
import {
  ANNOUNCEMENT_TYPES,
  AnnouncementType,
  IAnnouncement,
} from "models/IAnnouncement";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { setAnnouncements } from "../../../redux/announcementsSlice";
import { useAnnouncementsBannerStyles } from "./AnnouncementsBanner.styles";
import { useNotificationBannerLogic } from "hooks/useNotificationBannerLogic";

type IAnnouncementWithId = IAnnouncement & {
  id: string;
  type: AnnouncementType;
};

const DEFAULT_FETCH_FN = async (): Promise<IAnnouncementWithId[]> => {
  const data = await getAnnouncements({ onlyEnabled: true, onlyNotSeen: true });
  return data as IAnnouncementWithId[];
};
const DEFAULT_MARK_SEEN_FN = (id: string): Promise<void> =>
  markAnnouncementAsSeen({ id });

export interface IAnnouncementsBannerProps {
  fetchFn?: () => Promise<IAnnouncementWithId[]>;
  markSeenFn?: (id: string) => Promise<void>;
}

export const AnnouncementsBanner = ({
  fetchFn = DEFAULT_FETCH_FN,
  markSeenFn = DEFAULT_MARK_SEEN_FN,
}: IAnnouncementsBannerProps = {}): JSX.Element => {
  const styles = useAnnouncementsBannerStyles();
  const dispatch = useDispatch();
  const isDefaultMode = fetchFn === DEFAULT_FETCH_FN;

  const handleLoaded = useCallback(
    (items: IAnnouncementWithId[]) => {
      if (isDefaultMode) dispatch(setAnnouncements(items as IAnnouncement[]));
    },
    [dispatch, isDefaultMode],
  );
  const handleDismissed = useCallback(
    (_id: string, remaining: IAnnouncementWithId[]) => {
      if (isDefaultMode)
        dispatch(setAnnouncements(remaining as IAnnouncement[]));
    },
    [dispatch, isDefaultMode],
  );

  const { isLoading, visibleItems, handleDismiss, sanitizedContent } =
    useNotificationBannerLogic<IAnnouncementWithId>({
      fetchFn,
      markSeenFn,
      onLoaded: handleLoaded,
      onDismissed: handleDismissed,
    });

  if (isLoading || visibleItems.length === 0) {
    return <></>;
  }

  return (
    <div
      className={styles.container}
      role="region"
      aria-label="Announcements"
      aria-live="polite"
      aria-atomic="false"
      aria-relevant="additions removals"
    >
      {visibleItems.map((announcement: IAnnouncementWithId): JSX.Element => {
        const isWarning = announcement.type === ANNOUNCEMENT_TYPES.WARNING;

        return (
          <div
            key={announcement.id}
            className={`${styles.banner} ${isWarning ? styles.bannerWarning : styles.bannerInformational}`}
            role="alert"
            aria-label={`${announcement.type} announcement`}
          >
            <div
              className={isWarning ? styles.iconWarning : styles.iconInfo}
              aria-hidden="true"
            >
              {isWarning ? <Warning16Regular /> : <Info16Regular />}
            </div>
            <div
              className={isWarning ? styles.contentWarning : styles.contentInfo}
            >
              {announcement.title && (
                <span style={{ fontWeight: 600, marginRight: "4px" }}>
                  {announcement.title}:
                </span>
              )}
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizedContent[announcement.id] ?? "",
                }}
              />
            </div>
            <button
              className={isWarning ? styles.dismissWarning : styles.dismissInfo}
              onClick={() => void handleDismiss(announcement.id)}
              aria-label={`Dismiss announcement: ${announcement.title}`}
            >
              <Dismiss16Regular aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
